import React from "react";
import {
  address,
  businessHours,
  businessName,
  email,
  emailHref,
  faqItems,
  mapsUrl,
  phoneDisplay,
  phoneHref,
  serviceArea,
} from "./siteData";
const brandLogoSrc = "/assets/brand/logo.png";
const brandWordmarkSrc = "/assets/brand/FalseBayBannerNoLogo.png";

const navigation = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#expertise" },
  { label: "Commercial", href: "#commercial" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

const services = [
  {
    eyebrow: "Domestic Repairs",
    title:
      "Fridges, dishwashers, tumble dryers, and washing machines handled with care.",
    text: "Trusted diagnostics and repairs for fridges, tumble dryers, dishwashers, and washing machines, with a practical service approach built around real day-to-day use.",
    imageSrc: "/assets/images/services/DomesticRepairs.webp",
    imageAlt:
      "Technician repairing a refrigerator in a kitchen with other domestic appliances nearby.",
  },
  {
    eyebrow: "Air Conditioning",
    title: "Installations that bring comfort back fast.",
    text: "Supply and fit modern air conditioning systems for homes, offices, and service spaces that need reliable cooling.",
    imageSrc: "/assets/images/services/AirConditioning.webp",
    imageAlt:
      "Technician handing an air conditioner remote to a customer in a bright modern living room.",
  },
  {
    eyebrow: "Cold Rooms",
    title: "Custom-built freezer and cold rooms for serious storage.",
    text: "From insulated panel systems to temperature-control planning, installations are tailored to operational requirements and site conditions.",
    imageSrc: "/assets/images/services/ColdRoomsFreezerRooms.webp",
    imageAlt:
      "Commercial walk-in cold room with storage racks and staff reviewing the control panel.",
  },
  {
    eyebrow: "Shop Refrigeration",
    title: "Display cases and retail refrigeration kept trading.",
    text: "Service and maintenance support for deli cases, display refrigeration, and commercial systems that cannot afford unnecessary downtime.",
    imageSrc: "/assets/images/services/ShopRefrigeration.webp",
    imageAlt:
      "Technician servicing a deli display refrigerator in an active retail environment.",
  },
  {
    eyebrow: "Ice Machines",
    title: "Service and repairs for commercial ice machines.",
    text: "Commercial ice machines serviced and repaired for dependable performance in bars, kitchens, restaurants, and hospitality spaces.",
    imageSrc: "/assets/images/services/IceMachines.webp",
    imageAlt:
      "Commercial ice machine filled with fresh ice while a scoop collects cubes.",
  },
  {
    eyebrow: "Convenience",
    title: "Fetch-and-deliver support when timing matters.",
    text: "Flexible collection and return arrangements help keep appliance repairs practical for households and busy businesses.",
    imageSrc: "/assets/images/services/FetchAndDeliver.webp",
    imageAlt:
      "Technician loading an appliance into a service van outside a residential property for fetch-and-deliver support.",
  },
];

const expertisePoints = [
  "More than 35 years of experience in domestic and commercial refrigeration and air-conditioning.",
  "Domestic and commercial refrigeration service and repairs handled by one local team.",
  "Air-conditioning supply, installation, servicing, and repairs for homes and businesses.",
  "Appliance repairs backed by specialist refrigeration experience where technical cooling work is involved.",
];

const stats = [
  {
    value: "35+ Years",
    label: "Serving homes and businesses with long-standing local experience",
  },
  {
    value: "Appliance Repairs",
    label: "Fridges, tumble dryers, dishwashers, and washing machines",
  },
  {
    value: "Air Conditioning",
    label: "Supply, installation, and servicing for home and business comfort",
  },
  {
    value: "Based in Somerset West",
    label: "Servicing Gordon's Bay, Strand, Stellenbosch, and surrounding areas",
  },
];

const suppliers = [
  "Dunham-Bush",
  "TecsaReco",
  "Samsung",
  "Alliance",
  "Metraclark",
  "Macs Cool",
  "Claremont Home Appliances",
  "National Luna",
  "Scotsman",
  "Cape Lighthouse",
  "Frontline Electrical",
  "ACDC",
  "Apple Tool and Gas",
  "Bearing Man",
  "LG",
  "Defy",
  "Lead Laundry",
  "Snowparts",
];

const commercialHighlights = [
  "Walk-in cold and freezer room construction built around your operating requirements.",
  "Retail refrigeration and display-case servicing with a downtime-conscious approach.",
  "Ice machine and back-of-house cooling support for hospitality environments.",
];

function VisualPanel({
  eyebrow,
  title,
  prompt,
  imageSrc,
  imageAlt,
  tone = "cool",
  priority = false,
}) {
  return (
    <div className={`visual-panel visual-panel--${tone}`}>
      {imageSrc ? (
        <>
          <img
            className="visual-panel__image"
            src={imageSrc}
            alt={imageAlt || ""}
            width="1408"
            height="768"
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            decoding="async"
          />
          <div className="visual-panel__overlay" aria-hidden="true" />
        </>
      ) : (
        <>
          <div className="visual-panel__glow" aria-hidden="true" />
          <div className="visual-panel__grid" aria-hidden="true" />
        </>
      )}
      <div className="visual-panel__label" aria-hidden="true">{eyebrow}</div>
      <div className="visual-panel__title" aria-hidden="true">{title}</div>
      {prompt ? <p className="visual-panel__prompt">{prompt}</p> : null}
    </div>
  );
}

function App() {
  return (
    <div className="site-shell">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>

      <header className="site-header">
        <div className="brand-lockup">
          <a
            className="brand-name"
            href="#top"
            aria-label={`${businessName} home`}
          >
            <img
              className="brand-mark"
              src={brandLogoSrc}
              alt={`${businessName} logo`}
              width="2000"
              height="2000"
            />
            <img
              className="brand-wordmark"
              src={brandWordmarkSrc}
              alt={businessName}
              width="2048"
              height="512"
            />
          </a>
        </div>

        <nav className="site-nav" aria-label="Primary">
          {navigation.map((item) => (
            <a key={item.href} href={item.href}>
              {item.label}
            </a>
          ))}
        </nav>

        <a className="header-call" href={phoneHref}>
          Call Now
        </a>
      </header>

      <main id="main-content">
        <section className="hero-section section-frame" id="top">
          <div className="hero-copy">
            <p className="section-kicker">Sales, Spares and Repairs</p>
            <h1>
              Domestic and commercial refrigeration, air-conditioning and
              appliances
            </h1>
            <p className="hero-text">
              {businessName} provides services across{" "}
              {serviceArea}.
            </p>

            <div className="hero-actions">
              <a className="button button--primary" href={phoneHref}>
                {phoneDisplay}
              </a>
              <a className="button button--secondary" href="#services">
                Explore Services
              </a>
            </div>

            <ul className="hero-highlights" aria-label="Business highlights">
              <li>Fridges, tumble dryers, dishwashers, and washing machines</li>
              <li>
                Domestic and commercial air-conditioning supply, installation,
                servicing, and repairs
              </li>
              <li>
                Domestic and commercial refrigeration, cold rooms, and shop
                refrigeration
              </li>
            </ul>
          </div>

          <div className="hero-visual-wrap">
            <VisualPanel
              eyebrow="Home Comfort"
              imageSrc="/assets/images/services/AirConditioning.webp"
              imageAlt="Technician assisting a customer with an air conditioning installation in a bright living room."
              priority
            />
            <div className="hero-card">
              <p className="hero-card__label">Primary Contact</p>
              <a href={phoneHref}>{phoneDisplay}</a>
              <a className="hero-card__email" href={emailHref}>
                {email}
              </a>
              <p>{address}</p>
            </div>
          </div>
        </section>

        <section
          className="stats-strip section-frame"
          aria-label="Key business positioning"
        >
          {stats.map((stat) => (
            <article
              key={stat.value}
              className={`stat-card${
                stat.value === "35+ Years" ? " stat-card--badge" : ""
              }`}
            >
              <strong>{stat.value}</strong>
              <p>{stat.label}</p>
            </article>
          ))}
        </section>

        <section
          className="suppliers-section section-frame"
          aria-label="Certified suppliers"
        >
          <div className="section-heading">
            <p className="section-kicker">Certified Suppliers</p>
            <h2>
              Certified suppliers of trusted refrigeration, air-conditioning,
              and appliance brands.
            </h2>
            <p>
              {businessName} works with a wide range
              of recognised suppliers across domestic and commercial equipment.
            </p>
          </div>

          <div className="supplier-marquee" aria-hidden="true">
            <div className="supplier-track">
              {[...suppliers, ...suppliers].map((supplier, index) => (
                <span
                  key={`supplier-a-${supplier}-${index}`}
                  className="supplier-pill"
                >
                  {supplier}
                </span>
              ))}
            </div>
          </div>

          <div
            className="supplier-marquee supplier-marquee--reverse"
            aria-hidden="true"
          >
            <div className="supplier-track">
              {[
                ...suppliers.slice().reverse(),
                ...suppliers.slice().reverse(),
              ].map((supplier, index) => (
                <span
                  key={`supplier-b-${supplier}-${index}`}
                  className="supplier-pill"
                >
                  {supplier}
                </span>
              ))}
            </div>
          </div>

          <ul className="visually-hidden">
            {suppliers.map((supplier) => (
              <li key={`accessible-${supplier}`}>{supplier}</li>
            ))}
          </ul>
        </section>

        <section className="services-section section-frame" id="services">
          <div className="section-heading">
            <p className="section-kicker">Services</p>
            <h2>
              Refrigeration and air-conditioning support for homes and
              businesses.
            </h2>
            <p>
              From domestic appliance repairs to commercial refrigeration
              systems, {businessName} supports a wide
              range of cooling and repair needs.
            </p>
          </div>

          <div className="services-grid">
            {services.map((service, index) => (
              <article key={service.title} className="service-card">
                <VisualPanel
                  eyebrow={service.eyebrow}
                  prompt={service.prompt}
                  imageSrc={service.imageSrc}
                  imageAlt={service.imageAlt}
                  tone={index % 2 === 0 ? "cool" : "neutral"}
                />
                <div className="service-card__copy">
                  <p className="service-card__eyebrow">{service.eyebrow}</p>
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="expertise-section section-frame" id="expertise">
          <div className="section-heading">
            <p className="section-kicker">Why Choose Us</p>
            <h2>
              Practical service backed by domestic and commercial refrigeration
              experience.
            </h2>
          </div>

          <div className="expertise-layout">
            <div className="expertise-copy">
              <p>
                Whether the job is a household appliance repair, an
                air-conditioning installation, or a commercial refrigeration
                call-out, {businessName} brings more
                than 35 years of practical service and technical know-how to
                every job across {serviceArea}.
              </p>

              <ul className="expertise-list">
                {expertisePoints.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </div>

            <aside className="trust-panel">
              <p className="trust-panel__eyebrow">Contact Details</p>
              <h3>Phone, service area, and address.</h3>
              <dl>
                <div>
                  <dt>Phone</dt>
                  <dd>
                    <a href={phoneHref}>{phoneDisplay}</a>
                  </dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>
                    <a href={emailHref}>{email}</a>
                  </dd>
                </div>
                <div>
                  <dt>Service Area</dt>
                  <dd>{serviceArea}</dd>
                </div>
                <div>
                  <dt>Address</dt>
                  <dd>{address}</dd>
                </div>
              </dl>
            </aside>
          </div>
        </section>

        <section className="commercial-section section-frame" id="commercial">
          <div className="commercial-copy">
            <p className="section-kicker">Commercial & Installations</p>
            <h2>
              Commercial refrigeration support for businesses that depend on
              reliable cooling.
            </h2>
            <p>
              Cold rooms, freezer rooms, display systems, and ice machines are
              all supported for businesses that need dependable commercial
              refrigeration.
            </p>

            <ul className="commercial-list">
              {commercialHighlights.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <VisualPanel
            eyebrow="Retail Refrigeration"
            title="Commercial systems serviced with a practical, downtime-conscious approach."
            imageSrc="/assets/images/services/ShopRefrigeration.webp"
            imageAlt="Retail deli refrigeration unit being serviced while the store operates."
            tone="neutral"
          />
        </section>

        <section className="faq-section section-frame" id="faq">
          <div className="section-heading">
            <p className="section-kicker">Frequently Asked Questions</p>
            <h2>Helpful answers before you book a service.</h2>
            <p>
              Clear information about our appliance, air-conditioning, and
              refrigeration services.
            </p>
          </div>

          <div className="faq-list">
            {faqItems.map(({ question, answer }) => (
              <details className="faq-item" key={question}>
                <summary>{question}</summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="contact-section section-frame" id="contact">
          <div className="section-heading">
            <p className="section-kicker">Contact</p>
            <h2>
              Call directly for domestic or commercial refrigeration,
              air-conditioning, and appliance repairs.
            </h2>
          </div>

          <div className="contact-layout">
            <article className="contact-card contact-card--primary">
              <p className="contact-card__eyebrow">Best way to reach us</p>
              <a className="contact-phone" href={phoneHref}>
                {phoneDisplay}
              </a>
              <a className="contact-email" href={emailHref}>
                {email}
              </a>
              <p>
                Speak to {businessName} directly for
                repairs, installations, or domestic and commercial cooling
                support.
              </p>
            </article>

            <article className="contact-card">
              <p className="contact-card__eyebrow">Visit or locate us</p>
              <h3>{address}</h3>
              <p>Based in Somerset West and servicing {serviceArea}.</p>
              <dl className="business-hours">
                {businessHours.map(({ days, display }) => (
                  <div key={days}>
                    <dt>{days}</dt>
                    <dd>{display}</dd>
                  </div>
                ))}
              </dl>
              <a
                className="text-link"
                href={mapsUrl}
                target="_blank"
                rel="noreferrer"
              >
                Open in Maps
              </a>
            </article>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div>
          <div className="footer-brand-lockup">
            <img
              className="footer-brand-logo"
              src={brandLogoSrc}
              alt={`${businessName} logo`}
              width="2000"
              height="2000"
            />
            <img
              className="footer-brand-image"
              src={brandWordmarkSrc}
              alt={businessName}
              width="2048"
              height="512"
            />
          </div>
          <p>
            Domestic and commercial refrigeration, air-conditioning, and
            appliance repair services.
          </p>
        </div>
        <div className="footer-meta">
          <a href={phoneHref}>{phoneDisplay}</a>
          <a href={emailHref}>{email}</a>
          <p>{address}</p>
          <p>© {businessName}. All rights reserved.</p>
        </div>
      </footer>

      <a
        className="floating-call"
        href={phoneHref}
        aria-label={`Call ${businessName}`}
      >
        Call {phoneDisplay}
      </a>
    </div>
  );
}

export default App;
